// @flow
import React from 'react'
import { connect } from 'react-redux'
import { Route, withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import { parse, stringify } from 'query-string'
import { MuiThemeProvider } from '@material-ui/core/styles'
import Snackbar from '@material-ui/core/Snackbar'
import { Breadcrumbs, ContextBar } from 'catena/er2_ui'
import type { GeoBarType, MapToolbarType as CatenaMapToolbarType } from 'catena/er2_ui'
import { parseLocation } from 'er2_web_utils'
import AttributeTable from 'catena/er2_map_userlayers/js/components/attribute_table'
import UserLayersContainer from 'catena/er2_map_userlayers/js/containers/userlayers'
import TabbedPanel from 'catena/er2_map_userlayers/js/components/tabbed_panel'
import WindowState, { windowStates } from 'catena/er2_map_userlayers/js/components/window_state'
import muiTheme from 'catena/er2_styles/mui'
import type { AttributeTablesType } from 'catena/er2_map_userlayers/js/reducers/attribute_tables'
import type { IdentifyType } from 'catena/er2_map_userlayers/js/reducers/identify'
import type { MapType } from 'catena/er2_map_userlayers/js/reducers/map'
import type { MapBaseLayerType } from 'catena/er2_map_userlayers/js/reducers/mapbaselayer'
import type { MapfileType } from 'catena/er2_map'
import type { MapsourcesType } from 'catena/er2_map_userlayers/js/reducers/mapsources'
import type { ServicesType } from 'catena/er2_map_services/js/reducers/services'
import type { SitesType } from 'catena/er2_map_userlayers/js/reducers/public_sites'
import type { TabbedPanelType } from 'catena/er2_map_userlayers/js/reducers/tabbed_panel'
import * as Actions from '../actions'
import CollaborationPanel from '../components/collaboration'
import type { ErrorType } from '../reducers/error'
import type { SelectBookmarkBarType } from '../reducers/select_bookmark_bar'
import type { SelectFrameworkBarType } from '../reducers/select_framework_bar'
import type { AnalyzeFrameworkBarType } from '../reducers/analyze_framework_bar'
import type { HeaderType } from '../reducers/header'
import type { MetaType } from '../reducers/meta'
import type { AnalysisPanelType } from '../reducers/analysis_panel'
import type { CollaborationType } from '../reducers/collaboration'
import type { CollaborationSettingsType } from '../reducers/collaboration_settings'
import type { ContextBarItemType } from '../reducers/context_bar'
import type { DigpopType } from '../reducers/digpop'
import type { SettingsBarType } from '../reducers/settings'
import Instructions from '../components/instructions'
import Settings from '../components/settings'
import AnalyzeFrameworkBar from '../components/analyze_framework_bar'
import Closer from '../components/closer'
import SelectBookmarkBar from '../components/select_bookmark_bar'
import SelectFrameworkBar from '../components/select_framework_bar'
import FicusMap from '../components/ficus_map'
import FrameworkInfo from '../components/framework_info'
import MapLegend from '../components/map_legend'
import AnalysisPanel from '../components/analysis_panel'
import DemographicAnalysis from '../components/demographic_analysis'
import RefugeeModel from '../components/refugee_model/refugee_model'
import RefugeeModelChart from '../components/refugee_model/chart'
import RefugeeModelTabs from '../components/refugee_model/tabbed_panel'
import TableOfContents from '../components/table_of_contents'
import Transims from '../components/transims'
import * as ids from '../constants/ids'
import * as routes from '../constants/routes'
import { init as wsInit } from '../modules/websockets/client/actions/'
import { renderHeader } from './app_includes'
import * as colors from '../styles/colors'
import type { RefugeeModelType } from '../reducers/refugee_model'
import type { TransimsType } from '../reducers/transims'

type Props = {
    actions: {
        addBookmark: Function,
        addBookmarks: Function,
        clearErrorMessage: Function,
        clearFramework: Function,
        fetchBookmarks: Function,
        fetchSessions: Function,
        fetchSessionState: Function,
        fetchState: Function,
        fetchFrameworks: Function,
        generateGrid: Function,
        generateGridFromPoly: Function,
        getFicusInfo: Function,
        joinRoom: Function,
        handledClickMap: Function,
        handledClickRealizations: Function,
        handledSetFramework: Function,
        lookupPoint: Function,
        moveBookmark: Function,
        onChangeBaseLayer: Function,
        onChangeCollaborationInput: Function,
        onChangeInput: Function,
        onChangeLayerProps: Function,
        onChangeMapScale: Function,
        onClickContextBarItem: Function,
        onClickRealizations: Function,
        onJoinSession: Function,
        onLogout: Function,
        onShareSession: Function,
        onSingleClickMap: Function,
        onCloseContextBarItems: Function,
        onHideAttributeTable: Function,
        onOpenContextBarItems: Function,
        onRefugeeModelDisplayStatistics: Function,
        onTransimsRun: Function,
        onTransimsSetZoneColor: Function,
        onUserLayersAdd: Function,
        onUserLayerDelete: Function,
        onUserLayersDelete: Function,
        onUserLayersPropsChange: Function,
        onUserLayersSort: Function,
        onUserLayersZoom: Function,
        onWebsocketEvent: Function,
        removeMapLocation: Function,
        removeMapLocations: Function,
        setAnalysisData: Function,
        setAnalyzeMode: Function,
        setFicusConfig: Function,
        setFicusData: Function,
        setHeaderVisibility: Function,
        setRefugeeModelWindowState: Function,
        setTabbedPanelWindowState: Function,
        setTransimConfig: Function,
        showMapToolbar: Function,
        toggleAnalysisPanelVisible: Function,
        updateCollaborationSettings: Function,
        updateMapLocation: Function,
        updateRefugeeLayer: Function,
        updateTransimsLayer: Function,
        updateSettings: Function,
    },
    analyzeFrameworkBar: AnalyzeFrameworkBarType,
    analysisPanel: AnalysisPanelType,
    attributeTables: AttributeTablesType,
    classes: {},
    collaboration: CollaborationType,
    collaborationSettings: CollaborationSettingsType,
    contextBar: {
        items: Array<ContextBarItemType>,
    },
    digpop: DigpopType,
    error: ErrorType,
    geoBar: GeoBarType,
    header: HeaderType,
    history: {
        action: string,
        block: Function,
        length: number,
        push: Function,
        replace: Function,
        go: Function,
        goBack: Function,
        goForward: Function,
    },
    identify: IdentifyType,
    location: {
        pathname: string,
        search?: string,
        hash?: string,
    },
    map: MapType,
    mapBaseLayer: MapBaseLayerType,
    mapfile: MapfileType,
    mapsources: MapsourcesType,
    mapToolbar: CatenaMapToolbarType,
    meta: MetaType,
    publicSites: SitesType,
    refugeeModel: RefugeeModelType,
    selectBookmarkBar: SelectBookmarkBarType,
    selectFrameworkBar: SelectFrameworkBarType,
    serverState: {
        isFetched: boolean,
        isFetching: boolean,
        exception: {
            message: string,
            traceback: string,
        },
    },
    services: ServicesType,
    settingsBar: SettingsBarType,
    store: any,
    tabbedPanel: TabbedPanelType,
    tableOfContents: {
        items: Array<{
            id: string,
            link: string,
            name: string,
        }>,
    },
    tableOfContentsFramework: {
        items: Array<{
            id: string,
            link: string,
            name: string,
        }>,
    },
    token: {
        value: string,
        expires: number,
    },
    transims: TransimsType,
    version: string,
}

type StateType = {
    // True if the available sessions have been loaded
    isReady: boolean,
}

/* eslint-disable react/prefer-stateless-function */
class App extends React.Component<Props, StateType> {
    static defaultProps = {
        match: { params: {} },
        mapToolbar: { items: [] },
        tableOfContents: { items: [] },
    }

    constructor(props) {
        super(props)
        this.state = {
            isReady: false,
        }
    }

    componentDidMount() {
        // Fetch the application state once, on initial load
        const parsedLoc = parseLocation(location)
        if (!this.props.serverState.isFetched && !this.props.serverState.isFetching) {
            this.props.actions.fetchState(parsedLoc)
        }

        // Pass history so that we can redirect to the info help context
        this.props.actions.fetchFrameworks({ history: this.props.history })
        this.props.actions.fetchBookmarks()

        if (parsedLoc.path === routes.SELECT_FRAMEWORK ||
            parsedLoc.path === routes.SELECT_BOOKMARK ||
            parsedLoc.path === routes.DEMOGRAPHIC_ANALYSIS ||
            parsedLoc.path === routes.REFUGEE_MODEL ||
            parsedLoc.path === routes.TRANSIMS ||
            parsedLoc.path === routes.ANALYZE_FRAMEWORK) {
            if (parsedLoc.params.theme) {
                this.props.actions.setFicusData(parsedLoc.params.theme)
            }
        } else if (parsedLoc.path === routes.INSTRUCTIONS_FRAMEWORK) {
            this.props.actions.getFicusInfo(parsedLoc.params.infoNode)
        }

        // Initialize actions dispatched via incoming websocket messages
        wsInit(this.props.store, this.props.actions.onWebsocketEvent)
        this.props.actions.fetchSessions()

        this.props.actions.showMapToolbar(false)
    }

    componentWillReceiveProps(nextProps) {
        // Append the token to the URL to sync state, when available, but only if the user is not defined.
        const showTokenInUrl = nextProps.token.value && (nextProps.meta.user === undefined || nextProps.meta.user.id === null)
        if (showTokenInUrl) {
            const params = parse(this.props.location.search)
            if (params.token !== nextProps.token.value) {
                params.token = nextProps.token.value
                this.props.history.push({
                    search: stringify(params),
                    hash: this.props.location.hash,
                })
            }
        }

        if (!this.state.isReady && this.props.collaboration.sessions.items) {
            this.setState({ ...this.state, isReady: true })

            // Check for session requests
            const parsedLoc = parseLocation(location)
            if (parsedLoc.params.session) {
                // Check for existing sessions
                const sessions = this.props.collaboration.sessions
                if (sessions.items && sessions.items.length) {
                    // Join the session
                    const sessionToken = sessions.items[0].token
                    this.props.actions.onJoinSession(sessionToken)
                } else {
                    // No sessions exist, create it.
                    this.props.actions.onChangeInput(ids.COLLABORATION_SESSION_NAME, parsedLoc.params.session)
                    this.props.actions.onShareSession()
                }
            }
        }
    }

    logout = () => {
        this.props.actions.onLogout()
        window.location = '/login/'
    }


    render() {
        if (this.props.serverState.exception) {
            return (<div className={this.props.classes.root}>
                {renderHeader(this.props)}
                <div className={this.props.classes.mapCtr}>
                    <h2>Sorry, got an error:</h2>
                    <h4>{this.props.serverState.exception.message}</h4>
                    <pre>{this.props.serverState.exception.traceback}</pre>
                </div>
            </div>)
        }

        if (!this.props.token.value) {
            // Render loading page until the server state is available
            return (<div className={this.props.classes.root}>
                {renderHeader(this.props)}
                <ContextBar
                    items={this.props.contextBar.items}
                    state={this.props}
                    theme={this.props.classes}
                    token={this.props.token}
                    onClickContextBarItem={this.props.actions.onClickContextBarItem}
                />
                <div className={this.props.classes.loading}>
                    <i className="fa fa-cog fa-spin fa-3x fa-fw" />
                    <span className="sr-only">Loading...</span>
                </div>
            </div>)
        }

        const parsedLoc = parseLocation(location)

        const attributeTables = this.props.attributeTables.attributeTables.map(t => (<AttributeTable
            attributeTable={t}
            onClose={() => this.props.actions.onHideAttributeTable(t.key)}
            key={t.key}
            theme={this.props.classes}
            token={this.props.token}
            {...this.props.actions}
        />))

        const getError = () => {
            const message = this.props.error.message
            if (message) {
                return (
                    <Snackbar
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        autoHideDuration={4000}
                        ContentProps={{ style: { backgroundColor: colors.secondaryOne } }}
                        message={message}
                        open
                        onClose={() => this.props.actions.clearErrorMessage()}
                    />
                )
            }
            return null
        }

        const tocVisible = (location.pathname === routes.SELECT_FRAMEWORK && this.props.selectFrameworkBar.visible) ||
            (location.pathname === routes.SELECT_BOOKMARK && this.props.selectBookmarkBar.visible) ||
            (location.pathname === routes.ANALYZE_FRAMEWORK && this.props.analyzeFrameworkBar.visible) ||
            (location.pathname === routes.DEMOGRAPHIC_ANALYSIS && this.props.digpop.visible) ||
            (location.pathname === routes.REFUGEE_MODEL && this.props.refugeeModel.visible) ||
            (location.pathname === routes.TRANSIMS && this.props.transims.visible) ||
            (location.pathname === routes.SETTINGS && this.props.settingsBar.visible) ||
            (location.pathname === routes.COLLABORATE && this.props.collaboration.visible)

        const maptoolbarVisible = location.pathname === routes.SELECT_FRAMEWORK ||
            location.pathname === routes.SELECT_BOOKMARK ||
            location.pathname === routes.ANALYZE_FRAMEWORK ||
            location.pathname === routes.DEMOGRAPHIC_ANALYSIS ||
            location.pathname === routes.REFUGEE_MODEL ||
            location.pathname === routes.TRANSIMS ||
            location.pathname === routes.SETTINGS ||
            location.pathname === routes.COLLABORATE ||
            location.pathname === routes.MAP

        const sub = s => s.substr(1, s.length - 2)
        const mapPath = `/(${sub(routes.MAP)}|${sub(routes.SELECT_FRAMEWORK)}|${sub(routes.SELECT_BOOKMARK)}|${sub(routes.ANALYZE_FRAMEWORK)}|${sub(routes.DEMOGRAPHIC_ANALYSIS)}|${sub(routes.REFUGEE_MODEL)}|${sub(routes.TRANSIMS)}|${sub(routes.SETTINGS)}|${sub(routes.COLLABORATE)})/`
        const hasAnalysisPanel = this.props.analyzeFrameworkBar.mode === '_allRealizations' && this.props.selectFrameworkBar.ficusData.themeType === 'raster'
        const frameworksActive = parsedLoc.path === routes.SELECT_FRAMEWORK ||
            parsedLoc.path === routes.SELECT_BOOKMARK ||
            parsedLoc.path === routes.ANALYZE_FRAMEWORK

        const theMuiTheme = muiTheme(colors)
        return (
            <MuiThemeProvider theme={theMuiTheme}>
                <div className={this.props.classes.root} id={'app_root'}>
                    {this.props.header.visible && renderHeader(this.props, this.logout)}
                    <Closer
                        isOpen={this.props.header.visible}
                        moveLeft={!tocVisible}
                        onToggle={() => this.props.actions.setHeaderVisibility(!this.props.header.visible)}
                        settings={this.props.settingsBar}
                        theme={this.props.classes}
                        type="header"
                    />
                    <ContextBar
                        items={this.props.contextBar.items}
                        state={this.props}
                        theme={this.props.classes}
                        token={this.props.meta.user ? null : this.props.token}
                        onClickContextBarItem={this.props.actions.onClickContextBarItem}
                    />
                    {getError()}
                    <Route
                        path={routes.HOME}
                        exact
                        render={() => (
                            <Breadcrumbs
                                header={{
                                    id: ids.SUBHEADER_DOCS,
                                    name: 'Overview',
                                }}
                                items={[]}
                                theme={this.props.classes}
                            />)}
                    />
                    <Route
                        path={routes.INSTRUCTIONS}
                        render={() => (
                            <Breadcrumbs
                                header={{
                                    id: ids.SUBHEADER_DOCS,
                                    name: 'Overview',
                                }}
                                items={[]}
                                theme={this.props.classes}
                            />)}
                    />

                    {maptoolbarVisible && false && (
                        <Closer
                            isOpen={tocVisible}
                            settings={this.props.settingsBar}
                            type="contextBar"
                            onToggle={toOpen => (toOpen ? this.props.actions.onOpenContextBarItems() : this.props.actions.onCloseContextBarItems())}
                        />
                    )}

                    {this.props.selectFrameworkBar.visible &&
                    <Route
                        path={routes.SELECT_FRAMEWORK}
                        render={() => (
                            <Breadcrumbs
                                header={{
                                    id: ids.SUBHEADER_SELECT_FRAMEWORK,
                                    name: 'Themes',
                                }}
                                items={[]}
                                theme={this.props.classes}
                            />)}
                    />
                    }
                    <Route
                        path={routes.SELECT_FRAMEWORK}
                        render={() => (
                            <SelectFrameworkBar
                                visible={this.props.selectFrameworkBar.visible}
                                collaboration={this.props.collaboration}
                                collaborationSettings={this.props.collaborationSettings}
                                ficus={this.props.selectFrameworkBar}
                                frameworkException={this.props.selectFrameworkBar.exception}
                                frameworks={this.props.selectFrameworkBar.frameworks}
                                settings={this.props.settingsBar}
                                theme={this.props.classes}
                                {...this.props.actions}
                            />)
                        }
                    />

                    {this.props.selectBookmarkBar.visible &&
                    <Route
                        path={routes.SELECT_BOOKMARK}
                        render={() => (
                            <Breadcrumbs
                                header={{
                                    id: ids.SUBHEADER_SELECT_BOOKMARK,
                                    name: 'Bookmarks / History',
                                }}
                                items={[]}
                                theme={this.props.classes}
                            />)}
                    />
                    }
                    <Route
                        path={routes.SELECT_BOOKMARK}
                        render={() => (
                            <SelectBookmarkBar
                                bookmarksLocal={this.props.selectBookmarkBar.bookmarksLocal}
                                bookmarksGlobal={this.props.selectBookmarkBar.bookmarksGlobal}
                                bookmarksMRU={this.props.selectBookmarkBar.bookmarksMRU}
                                ficus={this.props.selectFrameworkBar}
                                settings={this.props.settingsBar}
                                theme={this.props.classes}
                                visible={this.props.selectBookmarkBar.visible}
                                {...this.props.actions}
                            />)
                        }
                    />

                    {this.props.analyzeFrameworkBar.visible &&
                    <Route
                        path={routes.ANALYZE_FRAMEWORK}
                        render={() => (
                            <Breadcrumbs
                                header={{
                                    id: ids.SUBHEADER_ANALYZE_FRAMEWORK,
                                    name: 'Analysis',
                                }}
                                items={[]}
                                theme={this.props.classes}
                            />)}
                    />
                    }
                    {this.props.selectFrameworkBar.visible &&
                    <Route
                        path={routes.ANALYZE_FRAMEWORK}
                        render={() => (
                            <AnalyzeFrameworkBar
                                analysisPanel={this.props.analysisPanel}
                                config={this.props.analyzeFrameworkBar}
                                ficus={this.props.selectFrameworkBar}
                                ficusData={this.props.ficusData}
                                map={this.props.map}
                                theme={this.props.classes}
                                token={this.props.token}
                                {...this.props.actions}
                            />)
                        }
                    />
                    }

                    <Route
                        path={routes.FRAMEWORK_INFO}
                        render={() => (
                            <Breadcrumbs
                                header={{
                                    id: ids.FRAMEWORK_INFO,
                                    name: 'Framework Info',
                                }}
                                items={[]}
                                theme={this.props.classes}
                            />)}
                    />

                    {this.props.selectFrameworkBar &&
                    <Route
                        path={routes.FRAMEWORK_INFO}
                        render={() => (
                            <FrameworkInfo
                                fetchingFrameworkInfo={this.props.selectFrameworkBar.fetchingFrameworkInfo}
                                ficusData={this.props.selectFrameworkBar.ficusData}
                                theme={this.props.classes}
                            />)
                        }
                    />
                    }

                    <Route
                        path={routes.TRANSIMS}
                        render={() => (
                            <Breadcrumbs
                                header={{
                                    id: ids.TRANSIMS,
                                    name: 'HISA (alpha)',
                                }}
                                items={[]}
                                theme={this.props.classes}
                            />)}
                    />

                    {this.props.transims.visible && this.catenaMap && (
                        <Route
                            path={routes.TRANSIMS}
                            render={() => (
                                <React.Fragment>
                                    <Transims
                                        ficusData={this.props.selectFrameworkBar.ficusData}
                                        er2Map={this.catenaMap.mapApp.map}
                                        generateGrid={this.props.actions.generateGrid}
                                        hasAnalysisPanel={hasAnalysisPanel}
                                        hasHeader={this.props.header.visible}
                                        onChangeProps={this.props.actions.onUserLayersPropsChange}
                                        onRun={this.props.actions.onTransimsRun}
                                        onSetZoneColor={this.props.actions.onTransimsSetZoneColor}
                                        settings={this.props.settingsBar}
                                        transims={this.props.transims}
                                        theme={this.props.classes}
                                        tocVisible={tocVisible}
                                        updateTransimsLayer={this.props.actions.updateTransimsLayer}
                                    />
                                    {this.props.refugeeModel.displayStatistics && (
                                        <WindowState
                                            windowState={this.props.refugeeModel.windowState}
                                            tocVisible={tocVisible}
                                            onChangeWindowState={this.props.actions.setRefugeeModelWindowState}
                                            theme={this.props.classes}
                                            type="south"
                                        />
                                    )}
                                    {this.props.refugeeModel.windowState !== windowStates.minimized && (
                                        <RefugeeModelTabs
                                            maptoolbarVisible={false}
                                            theme={this.props.classes}
                                            tocVisible={tocVisible}
                                            tabbedPanel={{ windowState: this.props.refugeeModel.windowState }}
                                        >
                                            {this.props.refugeeModel.displayStatistics && (
                                                <RefugeeModelChart
                                                    key="Results"
                                                    refugeeModel={this.props.refugeeModel}
                                                />
                                            )}
                                        </RefugeeModelTabs>
                                    )}
                                </React.Fragment>
                            )}
                        />
                    )}

                    <Route
                        path={routes.DEMOGRAPHIC_ANALYSIS}
                        render={() => (
                            <Breadcrumbs
                                header={{
                                    id: ids.DEMOGRAPHIC_ANALYSIS,
                                    name: 'Demographic Analysis (alpha)',
                                }}
                                items={[]}
                                theme={this.props.classes}
                            />)}
                    />

                    {this.props.digpop.visible && this.catenaMap && (
                        <Route
                            path={routes.DEMOGRAPHIC_ANALYSIS}
                            render={() => (
                                <DemographicAnalysis
                                    ficusData={this.props.selectFrameworkBar.ficusData}
                                    er2Map={this.catenaMap.mapApp.map}
                                    theme={this.props.classes}
                                    {...this.props.actions}
                                />)
                            }
                        />
                    )}

                    <Route
                        path={routes.REFUGEE_MODEL}
                        render={() => (
                            <Breadcrumbs
                                header={{
                                    id: ids.REFUGEE_MODEL,
                                    name: 'Refugee Model (alpha)',
                                }}
                                items={[]}
                                theme={this.props.classes}
                            />)}
                    />

                    {this.props.refugeeModel.visible && this.catenaMap && (
                        <Route
                            path={routes.REFUGEE_MODEL}
                            render={() => (
                                <React.Fragment>
                                    <RefugeeModel
                                        ficusData={this.props.selectFrameworkBar.ficusData}
                                        er2Map={this.catenaMap.mapApp.map}
                                        generateGridFromPoly={this.props.actions.generateGridFromPoly}
                                        hasAnalysisPanel={hasAnalysisPanel}
                                        hasHeader={this.props.header.visible}
                                        onChangeProps={this.props.actions.onUserLayersPropsChange}
                                        onDisplayStatistics={this.props.actions.onRefugeeModelDisplayStatistics}
                                        refugeeModel={this.props.refugeeModel}
                                        theme={this.props.classes}
                                        tocVisible={tocVisible}
                                        updateRefugeeLayer={this.props.actions.updateRefugeeLayer}
                                    />
                                    {this.props.refugeeModel.displayStatistics && (
                                        <WindowState
                                            windowState={this.props.refugeeModel.windowState}
                                            tocVisible={tocVisible}
                                            onChangeWindowState={this.props.actions.setRefugeeModelWindowState}
                                            theme={this.props.classes}
                                            type="south"
                                        />
                                    )}
                                    {this.props.refugeeModel.windowState !== windowStates.minimized && (
                                        <RefugeeModelTabs
                                            maptoolbarVisible={false}
                                            theme={this.props.classes}
                                            tocVisible={tocVisible}
                                            tabbedPanel={{ windowState: this.props.refugeeModel.windowState }}
                                        >
                                            {this.props.refugeeModel.displayStatistics && (
                                                <RefugeeModelChart
                                                    key="Results"
                                                    refugeeModel={this.props.refugeeModel}
                                                />
                                            )}
                                        </RefugeeModelTabs>
                                    )}
                                </React.Fragment>
                            )}
                        />
                    )}

                    {this.props.collaboration.visible && (
                        <Route
                            path={routes.COLLABORATE}
                            render={() => (
                                <Breadcrumbs
                                    header={{
                                        id: ids.SUBHEADER_COLLABORATE,
                                        name: 'Brushing and Linking',
                                    }}
                                    items={[]}
                                    theme={this.props.classes}
                                />)}
                        />
                    )}
                    <Route
                        path={routes.COLLABORATE}
                        render={() => (
                            <CollaborationPanel
                                collaboration={this.props.collaboration}
                                collaborationSettings={this.props.collaborationSettings}
                                theme={this.props.classes}
                                token={this.props.token}
                                updateSettings={this.props.actions.updateCollaborationSettings}
                                visible={this.props.collaboration.visible}
                                {...this.props.actions}
                            />)
                        }
                    />

                    {this.props.settingsBar.visible && (
                        <Route
                            path={routes.SETTINGS}
                            render={() => (
                                <Breadcrumbs
                                    header={{
                                        id: ids.SETTINGS,
                                        name: 'Settings',
                                    }}
                                    items={[]}
                                    theme={this.props.classes}
                                />)}
                        />
                    )}
                    {this.props.settingsBar.visible && (
                        <Route
                            path={routes.SETTINGS}
                            render={() => (
                                <Settings
                                    theme={this.props.classes}
                                    config={this.props.analyzeFrameworkBar}
                                    setConfig={this.props.actions.setFicusConfig}
                                    settings={this.props.settingsBar}
                                    updateSettings={this.props.actions.updateSettings}
                                    {...this.props.actions}
                                />)
                            }
                        />
                    )}

                    {hasAnalysisPanel && maptoolbarVisible && (
                        <Closer
                            isOpen={this.props.analysisPanel.visible}
                            moveLeft={!tocVisible}
                            onToggle={() => this.props.actions.toggleAnalysisPanelVisible()}
                            settings={this.props.settingsBar}
                            theme={this.props.classes}
                            type="analysisPanel"
                        />
                    )}
                    {hasAnalysisPanel && this.props.analysisPanel.visible && (
                        <Route
                            path={mapPath}
                            render={() => (
                                <AnalysisPanel
                                    analysisPanel={this.props.analysisPanel}
                                    config={this.props.analyzeFrameworkBar}
                                    theme={this.props.classes}
                                    tocVisible={tocVisible}
                                    {...this.props.actions}
                                />)
                            }
                        />
                    )}

                    <Route
                        path={mapPath}
                        render={() => (
                            <FicusMap
                                analysisPanel={this.props.analysisPanel}
                                baseLayer={this.props.mapBaseLayer.baseLayer}
                                collaboration={this.props.collaboration}
                                collaborationSettings={this.props.collaborationSettings}
                                extent={this.props.map.extent}
                                ficusData={this.props.selectFrameworkBar.ficusData}
                                ficusNode={this.props.selectFrameworkBar.ficusNode}
                                ficusControlData={this.props.analyzeFrameworkBar}
                                hasAnalysisPanel={hasAnalysisPanel && this.props.analysisPanel.visible}
                                hasHeader={this.props.header.visible}
                                frameworksActive={frameworksActive}
                                map={this.props.map}
                                mapfile={this.props.mapfile}
                                mapScale={this.props.map.mapView.mapScale}
                                mapsources={this.props.mapsources.mapsources}
                                onSingleClick={location.pathname === routes.ANALYZE_FRAMEWORK ? this.props.actions.onSingleClickMap : null}
                                ref={(mapApp) => {
                                    this.catenaMap = mapApp
                                }}
                                settings={this.props.settingsBar}
                                theme={this.props.classes}
                                tocVisible={tocVisible}
                                {...this.props.actions}
                            />)
                        }
                    />

                    <Route
                        path={mapPath}
                        render={() => (
                            <MapLegend
                                ficusData={this.props.selectFrameworkBar.ficusData}
                                hasAnalysisPanel={hasAnalysisPanel && this.props.analysisPanel.visible}
                                hasHeader={this.props.header.visible}
                                tocVisible={tocVisible}
                                placement={this.props.settingsBar.mapLegendPlacement}
                                settings={this.props.settingsBar}
                                theme={this.props.classes}
                            />)
                        }
                    />

                    {this.catenaMap &&
                    <Route
                        path={mapPath}
                        render={() => (
                            <UserLayersContainer
                                actions={this.props.actions}
                                baseImageURL={'/static/er2_ficus/dist'}
                                er2Map={this.catenaMap.mapApp.map}
                                geoBar={this.props.geoBar}
                                identify={this.props.identify}
                                map={this.props.map}
                                mapBaseLayer={this.props.mapBaseLayer}
                                mapsources={this.props.mapsources}
                                mapToolbar={this.props.mapToolbar}
                                publicSites={this.props.publicSites}
                                services={this.props.services}
                                theme={this.props.classes}
                                token={this.props.token}
                            />
                        )}
                    />
                    }

                    <Route
                        path={routes.HOME}
                        exact
                        render={() => <Instructions theme={this.props.classes} token={this.props.token} />}
                    />
                    <Route
                        path={routes.HOME}
                        exact
                        render={() => (
                            <TableOfContents
                                items={this.props.tableOfContents.items}
                                theme={this.props.classes}
                                token={this.props.token}
                            />
                        )}
                    />
                    <Route
                        path={routes.INSTRUCTIONS}
                        render={() => <Instructions theme={this.props.classes} token={this.props.token} />}
                    />
                    <Route
                        path={routes.INSTRUCTIONS}
                        render={() => (
                            <TableOfContents
                                items={this.props.tableOfContents.items}
                                theme={this.props.classes}
                                token={this.props.token}
                            />
                        )}
                    />
                    <Route
                        path={routes.FRAMEWORK_INFO}
                        render={() => (
                            <TableOfContents
                                items={this.props.tableOfContentsFramework.items}
                                theme={this.props.classes}
                                token={this.props.token}
                            />
                        )}
                    />

                    {attributeTables.length > 0 && (
                        <React.Fragment>
                            <Route
                                path={mapPath}
                                render={() => (
                                    <WindowState
                                        windowState={this.props.tabbedPanel.windowState}
                                        tocVisible={tocVisible}
                                        onChangeWindowState={this.props.actions.setTabbedPanelWindowState}
                                        theme={this.props.classes}
                                        type="south"
                                    />
                                )}
                            />
                            {this.props.tabbedPanel.windowState !== windowStates.minimized && (
                                <Route
                                    path={mapPath}
                                    render={() => (
                                        <TabbedPanel
                                            maptoolbarVisible={this.props.mapToolbar.panel.visible}
                                            theme={this.props.classes}
                                            tabbedPanel={this.props.tabbedPanel}
                                            tocVisible={tocVisible}
                                        >
                                            {attributeTables}
                                        </TabbedPanel>
                                    )}
                                />
                            )}
                        </React.Fragment>
                    )}
                </div>

            </MuiThemeProvider>
        )
    }
}

const mapStateToProps = state => ({
    analyzeFrameworkBar: state.analyzeFrameworkBar,
    analysisPanel: state.analysisPanel,
    attributeTables: state.attributeTables,
    chartData: state.chartData,
    collaboration: state.collaboration,
    collaborationSettings: state.collaborationSettings,
    contextBar: state.contextBar,
    digpop: state.digpop,
    error: state.error,
    exception: state.exception,
    geoBar: state.geoBar,
    header: state.header,
    identify: state.identify,
    map: state.map,
    mapBaseLayer: state.mapBaseLayer,
    mapfile: state.mapfile,
    mapsources: state.mapsources,
    mapToolbar: state.mapToolbar,
    meta: state.meta,
    publicSites: state.publicSites,
    refugeeModel: state.refugeeModel,
    selectBookmarkBar: state.selectBookmarkBar,
    selectFrameworkBar: state.selectFrameworkBar,
    serverState: state.serverState,
    services: state.services,
    settingsBar: state.settingsBar,
    tabbedPanel: state.tabbedPanel,
    tableOfContents: state.tableOfContents,
    tableOfContentsFramework: state.tableOfContentsFramework,
    token: state.token,
    transims: state.transims,
})

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(Actions, dispatch),
})

export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps,
)(App))
